/**
 * LEARN — the chapters, their source reading, and the method being used on you.
 *
 * WHAT IT REPLACES. The mode said "Chapters and worked examples" and rendered a fleet comparison
 * table with a hull-girder section. Good surfaces, but ship DATA — the same defect Practice had: a
 * tab telling a candidate something the surface does not deliver. Those two panels belong under
 * Ship, where the geometry lives, and this mode now shows what its own label promises.
 *
 * ── READING IS QUOTED, AND THE ENGINE SAYS WHY ────────────────────────────────────────────────
 * `/api/reading` returns source text with a citation, a shelf and a note of its own:
 *
 *     "source text, quoted as source text — it teaches, it is never the rule (FP-018)"
 *
 * That distinction is the whole pedagogy and it is shown, not summarised. Every passage renders as a
 * CITED value, so a candidate can see he is reading Bowditch rather than reading us — and can go to
 * Bowditch. A course that paraphrases its sources into house prose teaches the house, and when the
 * house is wrong there is nothing underneath to catch it.
 *
 * ── THE METHOD IS SHOWN TO THE LEARNER, WHICH IS UNUSUAL AND DELIBERATE ───────────────────────
 * `/api/pedagogy/method` names the technique chosen for each chapter and WHY — spaced retrieval for
 * definitions that must become automatic, Fermi for checking a computed departure is the right size.
 * A candidate who knows he is being drilled rather than lectured studies differently. Hiding the
 * method is a habit of courses that would rather seem effortless than be understood.
 *
 * ── GAPS ARE COUNTED ON THE LADDER, SO THEY ARE SHOWN ─────────────────────────────────────────
 * Each ladder reports its own `gaps`. A chapter ladder that quietly presented 18 chapters while
 * three of them had nothing behind them would be the padding this system refuses everywhere else.
 */

import { useState, useEffect } from 'react';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';
import GatedPanel from './gated.jsx';
import EarthLab from './earthlab.jsx';
import ChartLab from './chartlab.jsx';
import CompassLab from './compasslab.jsx';
import SailingsLab from './sailingslab.jsx';
import CelestialLab from './celestiallab.jsx';
import SightsLab from './sightslab.jsx';
import MorseLab from './morselab.jsx';
import BlocksLab from './blockslab.jsx';
import CourierLab from './courierlab.jsx';
import ShapesLab from './shapeslab.jsx';
import PersistLab from './persistlab.jsx';
import IdentityLab from './identitylab.jsx';
import FailureLab from './failurelab.jsx';
import FiveLab from './fivelab.jsx';
import PredictorLab from './predictorlab.jsx';
import ContextLab from './contextlab.jsx';
import ToolLab from './toollab.jsx';
import AgentLab from './agentlab.jsx';
import RagLab from './raglab.jsx';
import ForjaLab from './forjalab.jsx';
import CapstoneLab from './capstonelab.jsx';
import PermLab from './permlab.jsx';
import LoopLab from './looplab.jsx';
import ClaimLab from './claimlab.jsx';
import DpBuildLab from './dpbuildlab.jsx';
import DpFailureLab from './dpfailurelab.jsx';
import DpPolarLab from './dppolarlab.jsx';
import DpAsogLab from './dpasoglab.jsx';
import DpClaimLab from './dpclaimlab.jsx';
import DpThrusterLab from './dpthrusterlab.jsx';
import DpRefLab from './dpreflab.jsx';
import DpFmeaLab from './dpfmealab.jsx';
import HullWalkLab from './hullwalklab.jsx';
import HookLab from './hooklab.jsx';
import LabSlot from './labaccess.jsx';
import COURSES from '../../courses.json';
import { authHeaders } from './identity.mjs';
import PassageBody from './passage.jsx';
import LastFree from './lastfree.jsx';
import HullPanel from './hullpanel.jsx';
import AtlasPanel from './atlaspanel.jsx';
import CourseMenu from './coursemenu.jsx';

/* THE COMPONENTS THIS APP HAS. Which one appears on which chapter of which ladder is declared in
   lab-access.json, not decided here — see labaccess.jsx for why, and for the bug that made it
   necessary. This map exists so the declaration can stay data and never import a component. */
const LAB_COMPONENTS = { EarthLab, ChartLab, CompassLab, CelestialLab, SightsLab, MorseLab, SailingsLab, BlocksLab, CourierLab, ShapesLab, PersistLab, IdentityLab, FailureLab, FiveLab, PredictorLab, ContextLab, ToolLab, AgentLab, RagLab, ForjaLab, CapstoneLab, PermLab, LoopLab, ClaimLab, HookLab, DpBuildLab, DpFailureLab, DpPolarLab, DpAsogLab, DpClaimLab, DpThrusterLab, DpRefLab, DpFmeaLab, HullWalkLab };

const W = {
  /* THE CATALOGUE (2026-08-18). /api/ladders answers "which ladders exist"; this answers "what does
     this university teach" — grouped by declared domain, with the declared edges and what each
     course costs. Compiled server-side from the registry; this surface holds no course list. */
  catalogue: { slug: 'course-catalogue', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/catalogue', method: 'GET', field: 'domains' } },
  ladders: { slug: 'chapter-ladders', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/ladders', method: 'GET', field: 'ladders' } },
  reading: { slug: 'chapter-reading', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/reading', method: 'GET', field: 'reading' } },
  /* THE CHAPTER'S OWN TEXT (2026-08-18). Founder: "i cant see in chapter 7". The reading widget
     below draws on the READING SHELF — Bowditch, Knight, cited source text — and returned nothing
     for chapter 7 while the corpus held twenty-two teaching entries for it. Seeded is not surfaced. */
  text:    { slug: 'chapter-text', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/chapter-text', method: 'GET', field: 'teaching' } },
  method:  { slug: 'pedagogic-method', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/pedagogy/method', method: 'GET', field: 'method' } },
  /* WHERE THE NO-FEE BOUNDARY IS, asked rather than assumed. The server publishes free_chapters per
     track so the surface never holds a second copy of a founder's pricing ruling — the drift this
     estate spent 2026-08-16 removing from five other places. */
  enrol:   { slug: 'enrolment-state', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/enrolment', method: 'GET', field: 'policy' } },
  /* Chapter TITLES. /api/ladders reports a COUNT, not a list, so a ladder alone can only offer
     "Chapter 7" — which tells a candidate nothing about whether it is the one he needs. The MCQ
     catalogue carries the titles against the same numbering, so it is read for them. Two sources
     joined on chapter number, both real; the alternative was inventing names for our own chapters. */
  titles:  { slug: 'chapter-titles', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/mcq/catalog', method: 'GET', field: 'catalog' } },
};

/* The ladder levels and the catalogue tracks are the same ladders under different names. */
/* A LEVEL MISSING HERE FALLS BACK TO 'maritime', which is not a harmless default: the chapter
   TITLES in the picker would come from the 2M ladder and sit beside CodeAI101's own numbers. Same
   cross-ladder fault as the reading bug, one surface over. CODEAI101 added 2026-08-14. */
/* CLAUDECODE101 added 2026-08-15. The founder pressed the tab and got nothing: "still not working
   the tab press shows no claude content". This map was the FIFTH hand-kept list a course had to
   appear in, and the comment directly above — written yesterday for CODEAI101 — describes the very
   fault: a level missing here falls back to maritime. I verified the API with curl again and
   declared it fixed, which is precisely what the note at the reading hook below says not to do. */
/* ── DERIVED FROM THE COURSE REGISTRY (2026-08-15) ─────────────────────────────────────────────
   The founder: "we need to ensure we can add n number of courses... a way we dont forget".
   This was the last of six hand-kept maps and the one that kept the Claude Code tab empty through
   three rounds of fixes. A level missing here falls back to maritime — which is why the fault was
   silent: the page rendered, with another ladder's chapter titles.
   The registry is imported as JSON so this stays a browser module; courses.mjs uses node:fs and
   cannot be bundled. */
const TRACK_FOR = Object.fromEntries(
  (COURSES.courses || []).filter((c) => c.status === 'ready').map((c) => [c.level, c.track]));

const pretty = (s) => String(s || '').replace(/_/g, ' ');

/* ── A CHAPTER MUST BE ADDRESSABLE ─────────────────────────────────────────────────────────────
 * FOUNDER, 2026-08-18: "i cant see in chapter 7" — and part of why was that there was no way to
 * SEND anyone to chapter 7. The mode was in the URL (?m=learn) and the chapter was not, so a
 * chapter existed only after somebody clicked, and could not be linked, bookmarked or filmed.
 *
 * demo-forge films the real page driven by URL alone, which is what stops a film drifting from the
 * product — it IS the product, photographed. A surface that cannot be reached by URL cannot be
 * filmed honestly, so this is the prerequisite for the video as much as for the link.
 *
 * Read once on mount and written on every change with replaceState, so the address bar always
 * reflects what is on screen without pushing a history entry per click. */
const readParam = (k) => {
  try { return new URLSearchParams(window.location.search).get(k); } catch { return null; }
};
const writeParams = (level, chapter) => {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('lvl', level);
    if (chapter) u.searchParams.set('ch', String(chapter)); else u.searchParams.delete('ch');
    window.history.replaceState({}, '', u);
  } catch { /* a URL we cannot write is not a reason to break the page */ }
};

export default function LearnSurface() {
  const [level, setLevel] = useState(() => readParam('lvl') || '2M');
  const [chapter, setChapter] = useState(() => {
    const n = Number(readParam('ch'));
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  /* The address bar follows the surface. Written in an effect rather than inside each setter so a
     chapter reached by any route — click, back button, or the picker — is equally linkable. */
  useEffect(() => { writeParams(level, chapter); }, [level, chapter]);

  const lad = useWidgetData(W.ladders, {}, { baseUrl: API, plan });
  const cat = useWidgetData(W.catalogue, {}, { baseUrl: API, plan });
  const ladders = lad.data?.ladders || [];
  const rung = ladders.find((l) => l.level === level) || ladders[0];

  const titles = useWidgetData(W.titles, {}, { baseUrl: API, plan });
  const titleFor = (n) => {
    const track = TRACK_FOR[String(level).toUpperCase()] || 'maritime';
    return (titles.data?.[track]?.rows || []).find((r) => r.chapter === n)?.title || null;
  };

  /* LEVEL TRAVELS WITH THE CHAPTER. Both of these sent `{ chapter }` alone, and both endpoints
     default a missing level to 2M — so every ladder was served 2nd Mate content. Selecting Code101
     and opening its first lesson gave "The Earth, Position and Direction". Reported by the founder
     2026-08-14: "i tried 101, when i click 1st lesson it takes to second mates."
     The API was right the whole time and I had verified it by calling it with level=CODE101 —
     which tested the server and never tested what the app asks it for. A chapter number is not a
     key on its own; it needs its ladder, on the wire as well as in the data. */
  /* WHO IS ASKING TRAVELS WITH THE ASK (2026-08-16). These two calls went out anonymous, always —
     useWidgetData sent a content-type and nothing else — so a learner who had signed in and held a
     live enrolment was refused every gated chapter, because the request that asked for the chapter
     never said who he was. The token was in localStorage the whole time and nothing sent it.
     Reported by the founder on his own paid ladder as "it logs and throws out": Progress knew him,
     since that call builds its headers by hand, and the chapters did not.
     Same family as the level-travels-with-the-chapter fault above — the server was right and the
     app was asking the wrong question. Verifying the API with a token proves nothing about what the
     APP sends. */
  const auth = authHeaders();
  const reading = useWidgetData(W.reading, chapter ? { chapter, level } : {},
                                { baseUrl: API, plan, enabled: Boolean(chapter), headers: auth });
  const method = useWidgetData(W.method, chapter ? { chapter, level } : {},
                               { baseUrl: API, plan, enabled: Boolean(chapter), headers: auth });
  const text = useWidgetData(W.text, chapter ? { chapter, level } : {},
                             { baseUrl: API, plan, enabled: Boolean(chapter), headers: auth });
  const policy = useWidgetData(W.enrol, {}, { baseUrl: API, plan, headers: auth });

  if (chapter) {
    const r = reading.data || {};
    const m = method.data || {};
    /* The no-fee boundary for THIS ladder, from the server. `undefined` when the policy call has
       not landed or the track has none — in which case LastFree renders nothing, which is the right
       failure: a panel about a boundary must not appear when the boundary is unknown. */
    /* `policy.data` is the WHOLE response, not the `field` named in the widget: useWidgetData only
       extracts a field for GraphQL wires and returns the entire body for REST ones. Declaring
       `field: 'policy'` and then reading `data.free_chapters` gave undefined, and the panel simply
       never rendered — no error, no warning, exactly the silent nothing a wrong field produces.
       Caught by opening the page rather than by reading the code, which is chapter 20's sixth
       mistake arriving inside the fix for chapter 4. */
    const freeHere = policy.data?.policy?.free_chapters?.[String(level).toLowerCase()];
    const lastFree = Array.isArray(freeHere?.chapters) && freeHere.chapters.length
      ? Math.max(...freeHere.chapters) : null;
    return (
      <div className="shell-col shell-wide g-rise">
        <div className="g-glass shell-panel">
          <div className="learn-head">
            <div>
              <div className="g-label">{r.level || level} · chapter {chapter}</div>
              <h2 className="g-h2" style={{ marginTop: 'var(--g-2)' }}>{r.title || m.title || ''}</h2>
            </div>
            <div className="shell-picker">
              {/* WHERE THE QUESTIONS ARE — founder, 2026-08-18: "where are mcq for these chapter 7".
                  They existed in three places and none of them was the chapter he was reading. A
                  candidate who has just read a chapter should not have to know that Practice is a
                  separate mode and find the same chapter again in a second list.
                  Two links because they are two different things: the paper is every question the
                  chapter can support, laid out to read or print; Practice serves ten and marks them. */}
              <a className="g-btn" href={`/question-paper?chapter=${chapter}&track=${TRACK_FOR[String(level).toUpperCase()] || 'maritime'}`}
                 target="_blank" rel="noopener noreferrer">Whole paper ↗</a>
              <button className="g-btn" onClick={() => setChapter(null)}>All chapters</button>
            </div>
          </div>

          {/* The method, and the reason for it, shown to the person it is being used on. */}
          {m.primary && (
            <div className="learn-method">
              <div className="g-label">Method</div>
              <div className="learn-method-row">
                <span className="g-chip g-chip-computed">{pretty(m.primary)}</span>
                {(m.also || []).map((x) => (
                  <span key={x} className="g-chip g-chip-cited">{pretty(x)}</span>
                ))}
              </div>
              {m.why && <p className="g-body" style={{ marginTop: 'var(--g-2)' }}>{m.why}</p>}
              {/* WHO STANDS BEHIND THE METHOD. A seed serves — it is better than no method at all —
                  but it is a proposal derived from the catalogue, not a judgement a Master Mariner
                  made, and a candidate reading "you are being drilled by spaced retrieval because…"
                  should be able to tell the difference. Attested says who and on what warrant, since
                  sea experience and examination experience are different grounds for the same
                  sentence. */}
              <p className="g-body g-dim learn-method-who">
                {m.seed
                  ? 'Provisional — seeded from the method catalogue, not yet reviewed by an attestor.'
                  : m.why_seed
                    ? `The method is declared${m.attested_by ? ` and attested by ${m.attested_by.replace(/-/g, ' ')}` : ''}; part of the reason above is provisional and awaits review.`
                    : m.attested_by
                    ? `Attested by ${m.attested_by.replace(/-/g, ' ')}${m.basis ? ` — ${[].concat(m.basis).join(', ')}` : ''}.`
                    : 'Declared in the method catalogue.'}
              </p>
            </div>
          )}
        </div>

        {/* SEE → MOVE → NAME before SOURCE. Chapter 1 is the first caller of the lab; the ordering
            is the founder's: "I don't want students scrolling through six enormous historical source
            extracts before they have formed the mental model." The passages are not removed — they
            follow, cited, and now describe something he has already done. */}
        {/* ONE SLOT, keyed on (level, chapter), replacing seven lines keyed on chapter number.
            The old form put a lab on that number of EVERY ladder: Master chapter 17 is Propulsion
            and Engineering Systems and was rendering the Morse light, Master 11 is Medical Care in
            Command and was rendering a sight-reduction instrument, Chief Mate 9 is Medical Care on
            Board and was rendering a navigation lab, and CODE101 chapter 1 — What Software Actually
            Is — was rendering a globe. The comment two lines above about not putting a navigation
            instrument in a stability chapter had the right instinct and only the wrong axis.

            It is also the seam the founder asked for: "labs stay free... but can we ultimate gate it
            later, only if they enroll". Gating is now one word on one row of lab-access.json. */}
        <LabSlot level={level} chapter={chapter} components={LAB_COMPONENTS}
                 api={API} gatedPanel={GatedPanel} />

        {/* THE SHIP THIS CHAPTER IS TAUGHT ON — founder, 2026-08-17: "chapter picks the hull that
            shows the point", after he saw the eight ships in the bottom bar and asked why none of
            them appear in the Second Mate chapters. They did not: the ladder named no ship in any of
            its twenty-one chapters, and chapters 6 and 7 returned an empty reading list as well.

            ABOVE the reading, deliberately, and for the same reason the lab is: SEE before SOURCE.
            A candidate who has looked at two hulls carrying 61,004 and 20,500 tonnes on the same
            176 m reads the passage about block coefficient as a description of something he has
            already seen. Renders nothing at all on the sixteen chapters that declare no hull. */}
        <HullPanel level={level} chapter={chapter} />
        {/* The ship the chapter teaches on, in three dimensions — declared per chapter on the
            ladder, computed never generated, and rendering its own refusal where no view applies. */}
        <AtlasPanel level={level} chapter={chapter} />

        {/* THE CHAPTER'S OWN TEXT — founder, 2026-08-18: "i cant see in chapter 7".
            The Reading panel below draws on the reading SHELF (Bowditch, Knight, cited source text)
            and returned nothing for chapter 7 while the corpus held twenty-two teaching entries for
            it. Seeding a module makes it examinable; it does not put it on a page. This is the page.

            Every entry carries its own attribution, so a contributed section reads as its author's —
            and the five that were edited say "corrected via Capt Anil Sharma" rather than hiding the
            editor. Teaching entries only: an entry carrying a deterministic clause is what the PAPER
            keys on, and showing the key beside the teaching would hand a candidate the answer. */}
        {(text.data?.teaching || []).length > 0 && (
          <div className="g-glass shell-panel">
          {/* A PANEL TITLE IS NOT A LABEL. These read at 11px uppercase — the same weight as the
              metadata beside them — so nothing sat between the page heading and the body and every
              panel arrived at one volume. g-h3 is the missing middle and it already existed:
              measured across 60 components, g-h3 was used 3 times against g-label 343. The words
              are unchanged; only the level is. */}
            <h3 className="g-h3">
              Chapter text · {text.data.teaching.length} entries
            </h3>
            <p className="g-body learn-note">
              Read and quoted, never the statement an answer is marked against.
            </p>
            <div className="learn-passages">
              {text.data.teaching.map((e) => (
                <article key={e.title} className="learn-passage">
                  <h3 className="g-body learn-p-title">{e.title}</h3>
                  <p className="g-body learn-quote">{e.body}</p>
                  {e.citation && (
                    <p className="g-body g-dim learn-p-note">{e.citation}</p>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}

        <div className="g-glass shell-panel">
          <h3 className="g-h3">
            Reading{Array.isArray(r.reading) ? ` · ${r.reading.length} passages` : ''}
          </h3>
          {/* The engine's own words about what reading IS. Not paraphrased: the distinction between
              source text and rule is the pedagogy, and softening it would remove it. */}
          {/* Only when there IS reading. With none, the note carries the REASON and is shown inside
              the refusal below — printing it here as well says the same sentence twice. */}
          {r.note && (r.reading || []).length > 0 && <p className="g-body learn-note">{r.note}</p>}

          {/* A GATED CHAPTER IS AN INVITATION, NOT AN ERROR. Rendered by the shared GatedPanel so
              Learn and Practice cannot drift in what they promise a learner. */}
          {reading.envelope?.state === 'gated'
            ? <div style={{ marginTop: 'var(--g-3)' }}>
                <GatedPanel envelope={reading.envelope} fallbackTitle={r.title} />
              </div>
            : reading.envelope ? <div style={{ marginTop: 'var(--g-3)' }}><Value of={reading.envelope} /></div>
            : reading.loading ? <div className="gl-empty g-body">Loading the reading…</div>
            : !(r.reading || []).length
              ? <div className="learn-noreading" style={{ marginTop: 'var(--g-3)' }}>
                  {/* ── "REFUSED" IS THE CONTRACT'S WORD, NOT A LEARNER'S ──────────────────────
                      FOUNDER, 2026-08-16, opening 2M chapter 7: "why second mate is being refused
                      to me". Nothing was refused — that chapter is inside the free tier and was
                      served. What he saw was the provenance REFUSED chip, which the engine uses to
                      mean "this system holds no value here" and which a reader reasonably takes to
                      mean "you may not have this".

                      He has made this point before, about the same chip on the gap count, and it
                      was fixed there and not here. So: the same fact, in his language. The state is
                      still REFUSED in the contract and the server's own reason is still quoted
                      verbatim — what changes is that the sentence now says WHAT IS MISSING before
                      it says why, and no chip claims something about the reader.

                      An honest disclosure nobody understands is not a disclosure. */}
                  <p className="g-body learn-noreading-h"><b>No source reading for this chapter yet.</b></p>
                  <p className="g-body g-dim">{r.note || 'No source passage is shelved for this chapter yet.'}</p>
                  <p className="g-body g-dim learn-noreading-ok">
                    The chapter itself is open to you — this is about the quoted source material
                    behind it, which the engine will not invent.
                  </p></div>
              : (
                <div className="learn-passages">
                  {r.reading.map((p, i) => (
                    <article key={i} className="learn-passage">
                      <h3 className="g-h3 learn-p-title">{p.title}</h3>
                      {/* The quote carries its citation as a PAIR — a passage a candidate cannot
                          trace is a passage he cannot check us on. */}
                      {/* The passage keeps its STRUCTURE. It used to be handed to Value as a bare
                          string, which flattened a command and its expected output into the
                          sentence around them — see passage.jsx. The citation is unchanged and
                          still attached: formatting must never be bought with provenance. */}
                      {p.quote
                        ? <div className="learn-quote"><Value of={cited(<PassageBody text={p.quote} />, p.citation || p.source)} /></div>
                        : <Value of={refused('this entry is shelved without a quotable passage')} />}
                      {/* A PASSAGE THAT SAYS "NOW" MEANS ITS OWN NOW. Measured 2026-08-13: one
                          served passage in thirty-nine asserts contemporaneity — "this method is
                          now the main-stay of the Navigator" — and until this line the year reached
                          the reader only as a dim shelf label beneath the quote. A candidate
                          revising for a 2026 examination should not have to infer that. The passage
                          is not withheld: it is good teaching about its own time, and rejecting it
                          would empty a chapter to fix a caption. */}
                      {p.dated_claim && (
                        <p className="learn-p-dated">
                          This passage says “{p.dated_claim.word}”
                          {p.dated_claim.year ? <> — it means <b>{p.dated_claim.year}</b>, not today.</>
                            : <> — it means its own time, not today.</>}
                          {' '}Check current practice before you rely on it in an examination.
                        </p>
                      )}
                      {/* A DIFFERENT COUNTRY'S DATUM IS NOT A DATE PROBLEM. Marmer wrote for the US
                          Coast and Geodetic Survey and reckons heights from mean lower low water;
                          this chapter asks for Chart Datum and its relation to LAT. Both correct,
                          different conventions — and a candidate who answers an oral fluently in the
                          wrong one fails it. Shown harder than the date note for that reason. */}
                      {p.convention_claim && (
                        <p className="learn-p-dated learn-p-convention">
                          This passage reckons from <b>{p.convention_claim.says}</b> — this chapter
                          examines you on <b>{p.convention_claim.chapter_asks_for}</b>. Read it for the
                          reasoning, not for the datum.
                        </p>
                      )}
                      <div className="learn-p-meta g-dim">
                        {p.shelf ? <span className="g-mono">{p.shelf}</span> : null}
                        {p.source ? <span>{p.source}</span> : null}
                      </div>
                      {p.note && <p className="g-body learn-p-note">{p.note}</p>}
                      {/* ── WHOSE RULE IS THIS? (2026-08-15) ────────────────────────────────
                          A United States regulation is not the rule an Indian CoC candidate is
                          examined on. Serving one as "the reading" without saying so would be a
                          confident wrong answer in the place a learner is least able to catch it —
                          so authority is stated on the passage itself, not in a footnote. */}
                      {p.authority === 'statute:US' && (
                        <p className="g-body learn-authority">
                          <b>United States regulation.</b> Instructive on how the matter is
                          regulated — <b>not the rule you are examined on</b> for an Indian
                          certificate.
                        </p>
                      )}
                      {p.authority === 'statute:IN' && (
                        <p className="g-body learn-authority learn-auth-in"><b>Indian statute.</b> This one is the rule.</p>
                      )}
                      {p.authority === 'convention' && (
                        <p className="g-body learn-authority"><b>International instrument.</b> Applies where adopted; check what your flag State has ratified.</p>
                      )}
                      {/* ── A SUMMARY THAT CITES A RULE IS NOT THE RULE (2026-08-15) ────────
                          ANKR's own ladder notes state the instruments rule by rule and cite them.
                          They are the most useful reading these chapters have — and marking them
                          'convention' would be passing our prose off as treaty text. A learner is
                          told plainly what they are holding and where the rule itself lives. */}
                      {p.authority === 'ankr' && (
                        <p className="g-body learn-authority learn-auth-ankr">
                          <b>ANKR's own note</b>, stating the rule it cites above.
                          {' '}<b>Read the instrument itself before you rely on it</b> — a summary
                          that cites a rule is not the rule.
                        </p>
                      )}
                      {p.authority === 'background' && p.binding !== 'declared' && (
                        <p className="g-body g-dim learn-p-note">A classic text — it teaches the principle, and is not the rule.</p>
                      )}
                    </article>
                  ))}
                </div>
              )}
        </div>

        {/* AT THE END OF THE LAST NO-FEE CHAPTER, and nowhere else. Placed after the reading rather
            than beside it: an invitation above the material is an advertisement, and the same words
            after it are an offer to somebody who has just been given something. Renders nothing
            unless the chapter IS the boundary — see lastfree.jsx. */}
        {/* The price comes from the SAME catalogue call the course menu reads, looked up by the
            level already on screen — not fetched again and not carried in a second piece of state.
            `undefined` while the call is in flight and `null` for a course with no ruled price;
            LastFree renders the money paragraph in neither case. */}
        <LastFree chapter={chapter} lastFree={lastFree} totalChapters={rung?.chapters ?? freeHere?.of ?? null}
          price={(cat.data?.domains || []).flatMap((d) => d.courses || []).find((c) => c.level === level)?.price || null} />
      </div>
    );
  }

  return (
    <div className="shell-col shell-wide g-rise">
      {/* THE MENU COMES FIRST, THEN THE LADDER. Founder, 2026-08-18: "ultimately the course menu is
          separate and then other ladders." A university is a catalogue of courses; a ladder is what
          you find inside one of them. Rendering the ladder first made the estate look like one
          course with seven names. */}
      <CourseMenu catalogue={cat.data} level={level} setLevel={setLevel} />

      <div className="g-glass shell-panel">
        <div className="g-label">The ladder</div>
        <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>Chapters, in the order they carry</h2>
        {/* "The first seven are foundations" was 2M's boundary written as if it were every
            ladder's. It is false for four of the seven — NavalArch101's five chapters are ALL
            foundation, Chief Mate has no foundation tier at all — and it sat above whichever
            ladder was selected. The invariant half is stated for everyone; the tier boundary is
            the ladder's own business and is shown on the chapter rows that carry it. */}
        <p className="g-body">
          A chapter is passed on two demonstrations at the ‘apply’ rung, never on having read it.
        </p>
        {/* THE PICKER THAT WAS A ROW. Seven equal buttons in which 2M sat beside CLAUDECODE101
            as if they were the same kind of thing. The catalogue below is compiled by
            /api/catalogue from the registry and the ladders' own declarations — this surface
            keeps no course list, no grouping and no fee logic. Falls back to the row only if the
            catalogue could not be compiled, so a menu outage never costs a learner the ladder. */}
        {cat.data?.domains?.length ? null : (
          <div className="shell-picker picker-even" style={{ marginTop: 'var(--g-3)' }}>
            {ladders.map((l) => (
              <button key={l.level} onClick={() => setLevel(l.level)}
                className={`g-btn ${level === l.level ? 'g-btn-primary' : ''}`}>
                {l.cert || l.level}
              </button>
            ))}
          </div>
        )}
      </div>

      {lad.envelope ? <div className="g-glass shell-panel"><Value of={lad.envelope} /></div> : (
        <div className="g-glass shell-panel learn-chapters-panel shell-span">
          <h3 className="g-h3">
            {rung ? `${rung.chapters} chapters` : 'Chapters'}
            {rung?.gaps ? ` · ${rung.gaps} with a declared gap` : ''}
          </h3>
          {/* A gap count is shown rather than absorbed. 18 chapters with three empty behind them is
              not 18 chapters, and presenting it as such would be the padding refused elsewhere. */}
          {/* FOUNDER, on a phone: "the REFUSED badge is visually attention-grabbing in a way that
              may confuse a first-time user." Correct, and the word was ours rather than his.
              REFUSED is the state name in the provenance CONTRACT and belongs on data surfaces
              where an engineer reads it. To a cadet opening the app it reads as an error he has
              caused. The FACT is unchanged and still stated plainly — three chapters are thin — but
              it is said in his language, because an honest disclosure nobody understands is not a
              disclosure. */}
          {rung?.gaps > 0 && (
            <p className="g-body learn-gap">
              <b>{rung.gaps} of these {rung.chapters} chapters are still thin.</b> We have less source
              material behind them than the rest, so they are counted here rather than quietly padded
              out. Everything shown is real; there is simply less of it.
            </p>
          )}
          <div className="learn-chapters">
            {Array.from({ length: rung?.chapters || 0 }, (_, i) => i + 1).map((n) => (
              <button key={n} className="learn-chapter" onClick={() => setChapter(n)}>
                <span className="learn-ch-n g-mono">{n}</span>
                {/* The title when it is known, the number when it is not — never a made-up name. */}
                <span className="learn-ch-title">{titleFor(n) || `Chapter ${n}`}</span>
                {n <= 7 && <span className="g-chip g-chip-cited">foundation</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
